import { z } from 'zod';

export const telephonyDirectionEnum = z.enum(['inbound', 'outbound']);
export const poolStatusEnum = z.enum(['pending', 'claimed', 'dismissed']);
export const clientMatchEnum = z.enum(['none', 'matched', 'ambiguous']);
export const recordingSyncEnum = z.enum(['none', 'pending', 'synced', 'failed', 'unavailable']);

// One Ozonetel call as the pool lists it. Phone numbers are shown only to
// staff who can claim; `clientName` is null until a client is matched or named.
export const callRowSchema = z.object({
  id: z.string(),
  direction: telephonyDirectionEnum,
  callerNumber: z.string().nullable(),
  callerNumberRaw: z.string().nullable(),
  did: z.string(),
  locationId: z.string().nullable(),
  locationName: z.string().nullable(),
  clientId: z.string().nullable(),
  clientMatch: clientMatchEnum,
  clientName: z.string().nullable(),
  clientStatus: z.string().nullable(),
  poolStatus: poolStatusEnum,
  claimedById: z.string().nullable(),
  // Defaulted rather than merely nullable: a client deployed ahead of the API
  // would otherwise fail to parse every row in the list.
  claimedByName: z.string().nullable().default(null),
  claimedAt: z.string().nullable(),
  sessionId: z.string().nullable(),
  ringStartedAt: z.string().nullable(),
  answeredAt: z.string().nullable(),
  endedAt: z.string().nullable(),
  connectedSeconds: z.number().int().nullable(),
  callStatus: z.string().nullable(),
  recordingSyncStatus: recordingSyncEnum,
  createdAt: z.string(),
});
export type CallRow = z.infer<typeof callRowSchema>;

export type PoolStatusFilter = 'pending' | 'claimed' | 'dismissed' | 'all';
export type OutcomeFilter = 'all' | 'answered' | 'missed' | 'claimable';

export interface CallListParams {
  page?: number;
  limit?: number;
  organizationId?: string;
  locationId?: string;
  poolStatus?: PoolStatusFilter;
  outcome?: OutcomeFilter;
  /** Inclusive window over the call's received time, as ISO instants. */
  from?: string;
  to?: string;
  /** Free text over the caller's name and number. */
  search?: string;
}

export interface PaginatedCalls {
  data: CallRow[];
  pagination: { page: number; limit: number; total: number };
}

export const claimCallInputSchema = z.object({
  clientId: z.string().min(1).optional(),
  firstName: z.string().trim().max(100).optional(),
  lastName: z.string().trim().max(100).optional(),
  isCrisis: z.boolean().optional(),
});
export type ClaimCallInput = z.infer<typeof claimCallInputSchema>;

export const claimCallResultSchema = z.object({
  callId: z.string(),
  sessionId: z.string(),
  clientId: z.string().nullable(),
});
export type ClaimCallResult = z.infer<typeof claimCallResultSchema>;

export const dismissReasonEnum = z.enum(['wrong_number', 'spam', 'test', 'duplicate', 'other']);
export type DismissReason = z.infer<typeof dismissReasonEnum>;

export const didMappingSchema = z.object({
  id: z.string(),
  did: z.string(),
  label: z.string().nullable(),
  // The CloudAgent campaign that owns this number. Click-to-call reads it, so
  // a psychologist is never asked to pick one.
  campaignName: z.string().nullable(),
  isActive: z.boolean(),
  businessAssociateId: z.string(),
  organizationId: z.string(),
  organizationName: z.string().nullable(),
  locationId: z.string(),
  locationName: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type DidMapping = z.infer<typeof didMappingSchema>;

export const didMappingInputSchema = z.object({
  // The admin types the ten national digits; the +91 is fixed in the form and
  // the server stores 91 + those ten, which is the shape Ozonetel sends.
  did: z
    .string()
    .trim()
    .regex(/^\d{10}$/, 'Enter the 10-digit number'),
  locationId: z.string().min(1, 'Pick a location'),
  campaignName: z.string().trim().max(255).optional(),
});
export type DidMappingInput = z.infer<typeof didMappingInputSchema>;

/**
 * PATCH body: every field optional, plus the activate/deactivate toggle. A
 * null label clears the column (the BE schema is nullable there), whereas an
 * omitted one leaves it alone.
 */
export type UpdateDidMappingInput = Partial<Omit<DidMappingInput, 'campaignName'>> & {
  campaignName?: string | null;
  isActive?: boolean;
};

export const sessionCategoryEnum = z.enum(['enquiry', 'counselling_session', 'wellness_checkin']);
export type SessionCategory = z.infer<typeof sessionCategoryEnum>;

export const SESSION_CATEGORY_LABELS: Record<SessionCategory, string> = {
  enquiry: 'Enquiry',
  counselling_session: 'Counselling session',
  wellness_checkin: 'Wellness check-in',
};

export const DISMISS_REASON_LABELS: Record<DismissReason, string> = {
  wrong_number: 'Wrong number',
  spam: 'Spam',
  test: 'Test call',
  duplicate: 'Duplicate',
  other: 'Other',
};

export const CALL_DIRECTION_LABELS: Record<z.infer<typeof telephonyDirectionEnum>, string> = {
  inbound: 'Inbound',
  outbound: 'Outbound',
};

/** Red flag axis on the session reports: flagged at all, by status, or never flagged. */
export const redFlagFilterEnum = z.enum(['any', 'open', 'under_review', 'closed', 'none']);
export type RedFlagFilter = z.infer<typeof redFlagFilterEnum>;
export const RED_FLAG_FILTER_LABELS: Record<RedFlagFilter, string> = {
  any: 'Flagged',
  open: 'Open flag',
  under_review: 'Flag under review',
  closed: 'Flag closed',
  none: 'Not flagged',
};

// Click-to-call. Agents come from the roster a super admin maintains; `state`
// and `since` are overlaid from the Ozonetel agent event feed when it is
// running, and are 'unknown'/null otherwise. `ready` false means the feed says
// the agent is busy.
export const dialOptionsSchema = z.object({
  agents: z.array(
    z.object({
      agentId: z.string(),
      label: z.string().nullish(),
      state: z.string(),
      since: z.string().nullable(),
      ready: z.boolean(),
    }),
  ),
});
export type DialOptions = z.infer<typeof dialOptionsSchema>;

export interface DialCallInput {
  clientId?: string;
  phoneNumber?: string;
  locationId?: string;
  agentId: string;
}

export const dialCallResultSchema = z.object({
  callId: z.string(),
  sessionId: z.string(),
  providerCallId: z.string(),
});
export type DialCallResult = z.infer<typeof dialCallResultSchema>;

/** Strip the stored 91 country code back to the ten digits the form edits. */
export const didNationalDigits = (did: string): string => {
  const digits = did.replace(/\D/g, '');
  return digits.length === 12 && digits.startsWith('91') ? digits.slice(2) : digits;
};

/** "+91 80496 31310" for display. */
export const formatDid = (did: string): string => {
  const national = didNationalDigits(did);
  return national.length === 10 ? `+91 ${national.slice(0, 5)} ${national.slice(5)}` : did;
};

// The Ozonetel agent roster, typed by a super admin to match CloudAgent exactly.
export const telephonyAgentSchema = z.object({
  id: z.string(),
  agentId: z.string(),
  label: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string().nullish(),
});
export type TelephonyAgent = z.infer<typeof telephonyAgentSchema>;

export const telephonyAgentInputSchema = z.object({
  agentId: z
    .string()
    .trim()
    .min(1, 'Enter the agent id exactly as it appears in Ozonetel')
    .max(100),
  label: z.string().trim().max(100).optional(),
});
export type TelephonyAgentInput = z.infer<typeof telephonyAgentInputSchema>;

export type UpdateTelephonyAgentInput = Partial<Omit<TelephonyAgentInput, 'label'>> & {
  label?: string | null;
  isActive?: boolean;
};
